import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { authToken } from '../globals/Global';
import { createEvents, deleteEvent, getAllEvents, updateEvent, importEvents } from '../globals/Services';

export default function EventList() {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', imageUrl: '' });
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [openEditEventDialog, setOpenEditEventDialog] = useState(false);
  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);

  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);

  const [refetch, setRefetch] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const csvInputRef = useRef(null);

  const triggerRefetch = () => setRefetch(prev => prev + 1);
  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  const fetchEventData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getAllEvents, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch data');
      const result = await res.json();
      const eventData = result?.data || [];

      const transformedEvents = eventData.map(evt => ({
        id: evt.id,
        title: evt.title,
        date: evt.date,
        imageUrl: evt.imageUrl,
        status: evt.status,
      }));
      setEvents(transformedEvents);

      if (!selectedEventId && transformedEvents.length > 0) {
        setSelectedEventId(transformedEvents[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch event data:', error);
      toast.error('Error fetching event data');
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    fetchEventData();
  }, [refetch, fetchEventData]);

  // ── CSV Upload ──────────────────────────────────────────────────────────────
  const handleCsvChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = '';

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(importEvents, {   // ← changed from IMPORT_EVENTS_URL
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData,
      });

      const result = await res.json();
      if (!res.ok || result.status !== 'SUCCESS') {
        throw new Error(result.errorMessage || 'Import failed.');
      }

      const count = result?.data?.length ?? 0;
      toast.success(`CSV imported successfully! ${count} event(s) added.`);
      triggerRefetch();
    } catch (error) {
      toast.error(`Import error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────

  const handleCreateEvent = async () => {
    try {
      const res = await fetch(createEvents, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          title: newEvent.title,
          date: newEvent.date,
          imageUrl: newEvent.imageUrl,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not create event.');
      toast.success('Event created successfully!');
      setOpenEventDialog(false);
      setNewEvent({ title: '', date: '', imageUrl: '' });
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    try {
      const res = await fetch(updateEvent, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          id: editingEvent.id,
          title: editingEvent.title,
          date: editingEvent.date,
          imageUrl: editingEvent.imageUrl,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not update event.');
      toast.success('Event updated successfully!');
      setOpenEditEventDialog(false);
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deletingEvent) return;
    try {
      const res = await fetch(deleteEvent(deletingEvent.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Could not delete event.');
      toast.success('Event deleted successfully!');
      setOpenDeleteEventDialog(false);
      setSelectedEventId(null);
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const openEditEvent = (evt) => {
    setEditingEvent(evt);
    setOpenEditEventDialog(true);
  };

  const openDeleteEvent = (evt) => {
    setDeletingEvent(evt);
    setOpenDeleteEventDialog(true);
  };

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">Event List</Typography>

        <Box display="flex" gap={1}>
          {/* Hidden file input — accepts CSV only */}
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCsvChange}
          />

          {/* Upload CSV button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            disabled={uploading}
            onClick={() => csvInputRef.current?.click()}
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          >
            {uploading ? 'Uploading…' : 'Upload CSV'}
          </Button>

          {/* Add Event button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenEventDialog(true)}
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add Event
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Card sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Typography color="text.secondary" mt={4}>Loading events...</Typography>
              ) : events.length > 0 ? (
                events.map(evt => (
                  <Box
                    key={evt.id}
                    onClick={() => setSelectedEventId(evt.id)}
                    sx={{
                      display: 'flex', gap: 2, p: 2, mb: 1,
                      border: 1, borderRadius: 2, cursor: 'pointer',
                      borderColor: selectedEventId === evt.id ? 'primary.main' : 'grey.300',
                      backgroundColor: selectedEventId === evt.id ? 'primary.lightest' : 'white'
                    }}
                  >
                    {evt.imageUrl && (
                      <Box component="img" src={evt.imageUrl} alt={evt.title} sx={{ width: 80, height: 80, borderRadius: 1 }} />
                    )}
                    <Box flexGrow={1}>
                      <Typography variant="body2" color="text.secondary">ID: {evt.id}</Typography>
                      <Typography fontWeight="bold">{evt.title}</Typography>
                      <Typography variant="body2" color="text.secondary">Date: {evt.date}</Typography>
                      <Typography variant="body2" color="text.secondary">Status: {evt.status}</Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditEvent(evt); }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteEvent(evt); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" mt={4}>No events found.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Create Event Dialog */}
      <Dialog open={openEventDialog} onClose={() => setOpenEventDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Event Title" margin="normal" value={newEvent.title} onChange={e => setNewEvent({ ...newEvent, title: e.target.value })} />
          <TextField fullWidth label="Event Date (e.g. 03/01/2025)" margin="normal" value={newEvent.date} onChange={e => setNewEvent({ ...newEvent, date: e.target.value })} />
          <TextField fullWidth label="Image URL" margin="normal" value={newEvent.imageUrl} onChange={e => setNewEvent({ ...newEvent, imageUrl: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEventDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateEvent} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={openEditEventDialog} onClose={() => setOpenEditEventDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Event</DialogTitle>
        {editingEvent && (
          <>
            <DialogContent>
              <TextField fullWidth label="Event Title" margin="normal" value={editingEvent.title} onChange={e => setEditingEvent({ ...editingEvent, title: e.target.value })} />
              <TextField fullWidth label="Event Date" margin="normal" value={editingEvent.date} onChange={e => setEditingEvent({ ...editingEvent, date: e.target.value })} />
              <TextField fullWidth label="Image URL" margin="normal" value={editingEvent.imageUrl} onChange={e => setEditingEvent({ ...editingEvent, imageUrl: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditEventDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateEvent} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteEventDialog} onClose={() => setOpenDeleteEventDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete: <strong>{deletingEvent?.title}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteEventDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteEvent} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
}